import defaultFetch from 'isomorphic-unfetch';
import type { QueryStringSerializer } from '@/scopes/common/infrastructure/query-strings/queryStringSerializer';
import { createQueryStringSerializer } from '@/scopes/common/infrastructure/query-strings/queryStringSerializer';
import type {
  LiveSearchApiResponse,
  LiveSearchQueryParams,
  LiveSearchResponse,
  LiveSearchResult,
} from '@/scopes/search/search/domain/live-search/types';

interface Dependencies {
  env: {
    NEXT_PUBLIC_API_BASE_URL: string;
  };
  fetch: typeof defaultFetch;
  queryStringSerializer: QueryStringSerializer;
  mapper: LiveSearchMapper;
  contextItemId: string;
}

interface LiveSearchMapper {
  map: (response: LiveSearchApiResponse) => LiveSearchResponse;
}

export const createLiveSearchApiClient = (dependencies: Partial<Dependencies> = {}) => {
  const {
    env = {
      NEXT_PUBLIC_API_BASE_URL: process.env.NEXT_PUBLIC_API_BASE_URL,
    },
    fetch = defaultFetch,
    queryStringSerializer = createQueryStringSerializer(),
    mapper = responseMapper(),
    contextItemId = '',
  } = dependencies;

  const fetchOptions: RequestInit = {
    referrerPolicy: 'no-referrer-when-downgrade',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
    },
  };

  async function getLiveSearchResults(queryParams: LiveSearchQueryParams) {
    if (queryParams.query === null || queryParams.query === '') {
      return null;
    }
    const url = buildAPIUrl(queryParams);

    const response = await fetch(url, fetchOptions);
    const json = await response.json();
    return mapper.map(json);
  }

  function buildAPIUrl(liveSearchQueryParams: LiveSearchQueryParams) {
    const { query } = liveSearchQueryParams;
    const queryString = queryStringSerializer.serialize({
      contextItemId,
      query,
    });

    return `${env.NEXT_PUBLIC_API_BASE_URL}/FreeTextSearch/GetFreeTextSearchApi?${queryString}`;
  }

  return { getLiveSearchResults };
};

export function responseMapper(): LiveSearchMapper {
  function map(response: LiveSearchApiResponse): LiveSearchResponse {
    const isDestination = (type: string) =>
      type === 'Country' || type === 'Region' || type === 'Subregion' || type === 'City';

    const { flightTickets, accommodations, destinations, information } = response.results.reduce(
      (acc, item) => {
        if (item.type === 'FlightTicket') {
          acc.flightTickets.push({
            name: item.name,
            url: decorateUrl(item.pageUrl, 'originList=live-search'),
            breadcrumbs: item.breadcrumbs,
          });
        }
        if (item.type === 'Accommodation') {
          acc.accommodations.push({
            name: item.name,
            url: decorateUrl(item.pageUrl, 'originList=live-search'),
            breadcrumbs: item.breadcrumbs,
          });
        }
        if (isDestination(item.type)) {
          acc.destinations.push({
            name: item.name,
            url: decorateUrl(item.searchUrl, 'originList=live-search'),
            breadcrumbs: item.breadcrumbs,
          });
        }
        if (isDestination(item.type) || item.type === 'ContentPage') {
          acc.information.push({
            name: item.name,
            url: decorateUrl(item.pageUrl, 'originList=live-search'),
            breadcrumbs: item.breadcrumbs,
          });
        }

        return acc;
      },
      {
        flightTickets: [] as LiveSearchResult[],
        accommodations: [] as LiveSearchResult[],
        destinations: [] as LiveSearchResult[],
        information: [] as LiveSearchResult[],
      }
    );

    return {
      ...(flightTickets.length && { flightTickets }),
      ...(accommodations.length && { accommodations }),
      ...(destinations.length && { destinations }),
      ...(information.length && { information }),
    };
  }

  return { map };
}

export const decorateUrl = (url: string | undefined, parameter: string) => {
  return url?.includes('?') ? `${url}&${parameter}` : `${url}?${parameter}`;
};
